import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ApiOutlined,
  DatabaseOutlined,
  LaptopOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  UserSwitchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { BodyComponent, RefreshButton } from "@comps/components";
import { makeRequest } from "@utils/helpers";
import {
  deleteAdmsUserReq,
  fetchAdmsAttendanceReq,
  getAdmsAttendanceReq,
  getAdmsCommandsReq,
  getAdmsMachinesReq,
  getAdmsMappingContextReq,
  getAdmsRequestsReq,
  getAdmsUsersReq,
  previewAdmsEmployeeSyncReq,
  queueAdmsCommandReq,
  removeAdmsMappingReq,
  saveAdmsMappingsReq,
  syncAdmsEmployeesReq,
  syncAdmsUsersReq,
  upsertAdmsUserReq,
} from "./requests";
import AdmsUserModal from "./components/AdmsUserModal";
import AdmsCommandModal from "./components/AdmsCommandModal";
import { convertTimeTotz } from "../../../../../wrappers/commonFunctions";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const makeMappingRow = () => ({
  key: `${Date.now()}-${Math.random()}`,
  employee_id: undefined,
  user_id: undefined,
});

const statusColors = {
  ready: "blue",
  map_existing_user: "gold",
  already_synced: "green",
  conflict: "red",
};

const privilegeLabelMap = {
  0: "User",
  2: "Enroller",
  6: "Admin",
  14: "Super Admin",
};

const pageSurface = {
  background: "linear-gradient(180deg, #f6f8fc 0%, #eef3fb 100%)",
  border: "1px solid #d9e4f5",
  borderRadius: 20,
  boxShadow: "0 18px 40px rgba(19, 48, 91, 0.08)",
};

const softCardStyle = {
  borderRadius: 18,
  border: "1px solid #dbe5f2",
  boxShadow: "0 10px 24px rgba(27, 55, 90, 0.06)",
};

const statShells = [
  { tint: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", border: "#bfdbfe", icon: <TeamOutlined style={ { fontSize: 18, color: "#1d4ed8" } } /> },
  { tint: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", border: "#ddd6fe", icon: <UserSwitchOutlined style={ { fontSize: 18, color: "#6d28d9" } } /> },
  { tint: "linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)", border: "#a5f3fc", icon: <DatabaseOutlined style={ { fontSize: 18, color: "#0f766e" } } /> },
  { tint: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)", border: "#fed7aa", icon: <ApiOutlined style={ { fontSize: 18, color: "#c2410c" } } /> },
];

const stickyPanelStyle = {
  position: "sticky",
  top: 16,
};

const viewportPanelHeight = "calc(100vh - 210px)";

const tabLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: 12,
  fontWeight: 600,
};

const IndexAdmsMachines = () => {
  const [loader, setLoader] = useState(false);
  const [detailLoader, setDetailLoader] = useState(false);
  const [actionLoader, setActionLoader] = useState(false);
  const [machines, setMachines] = useState([]);
  const [selectedMachineId, setSelectedMachineId] = useState(null);
  const [users, setUsers] = useState([]);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [commands, setCommands] = useState([]);
  const [requestLogs, setRequestLogs] = useState([]);
  const [mappingContext, setMappingContext] = useState({ employees: [], mappings: [] });
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [syncPreview, setSyncPreview] = useState({ rows: [], summary: null });
  const [mappingRows, setMappingRows] = useState([makeMappingRow()]);
  const [attendanceRange, setAttendanceRange] = useState([]);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [commandModalOpen, setCommandModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const detailRequestRef = useRef(0);

  useEffect(() => {
    loadMachines();
  }, []);

  useEffect(() => {
    if (!selectedMachineId) {
      setUsers([]);
      setAttendanceLogs([]);
      setCommands([]);
      setRequestLogs([]);
      setMappingContext({ employees: [], mappings: [] });
      setSyncPreview({ rows: [], summary: null });
      setSelectedEmployeeIds([]);
      setMappingRows([makeMappingRow()]);
      return;
    }

    setSyncPreview({ rows: [], summary: null });
    setSelectedEmployeeIds([]);
    setMappingRows([makeMappingRow()]);
    loadMachineDetails(selectedMachineId);
  }, [selectedMachineId]);

  const selectedMachine = useMemo(
    () => machines.find((machine) => machine.id === selectedMachineId) || null,
    [machines, selectedMachineId]
  );

  const selectedMachineLabel = selectedMachine?.name || selectedMachine?.code || "ADMS Console";

  const userFilterOptions = useMemo(
    () => users.map((user) => ({
      text: `${user.user_name} (${user.pin_manual})`,
      value: String(user.id),
    })),
    [users]
  );

  const mappedEmployeeFilterOptions = useMemo(
    () => users
      .filter((user) => user.machine_employee?.employee_name || user.employees?.[0]?.full_name)
      .map((user) => ({
        text: user.machine_employee?.employee_name || user.employees?.[0]?.full_name,
        value: String(user.machine_employee?.employee_id || user.employees?.[0]?.id || user.id),
      })),
    [users]
  );

  const mappingEmployeeFilterOptions = useMemo(
    () => (mappingContext.mappings || [])
      .filter((mapping) => mapping.employee_name || mapping.employee?.full_name || mapping.employee_id)
      .map((mapping) => ({
        text: mapping.employee_name || mapping.employee?.full_name || mapping.employee_id,
        value: String(mapping.employee_id),
      })),
    [mappingContext.mappings]
  );

  const mappingUserFilterOptions = useMemo(
    () => (mappingContext.mappings || [])
      .filter((mapping) => mapping.user_name_id)
      .map((mapping) => ({
        text: mapping.user_name_id,
        value: String(mapping.user_id),
      })),
    [mappingContext.mappings]
  );

  const employeeOptions = useMemo(
    () => {
      const mappedEmployeeIds = new Set((mappingContext.mappings || []).map((mapping) => String(mapping.employee_id)));
      const selectedEmployeeIdsInForm = new Set(mappingRows.map((row) => row.employee_id).filter(Boolean).map((value) => String(value)));

      return (mappingContext.employees || [])
        .filter((employee) => !mappedEmployeeIds.has(String(employee.employee_id)) || selectedEmployeeIdsInForm.has(String(employee.employee_id)))
        .map((employee) => ({
          label: `${employee.employee_name} (${employee.pin})`,
          value: employee.employee_id,
        }));
    },
    [mappingContext.employees, mappingContext.mappings, mappingRows]
  );

  const machineUserOptions = useMemo(
    () => {
      const mappedUserIds = new Set((mappingContext.mappings || []).map((mapping) => String(mapping.user_id)));
      const selectedUserIdsInForm = new Set(mappingRows.map((row) => row.user_id).filter(Boolean).map((value) => String(value)));

      return users
        .filter((user) => !mappedUserIds.has(String(user.id)) || selectedUserIdsInForm.has(String(user.id)))
        .map((user) => ({
          label: `${user.user_name} (${user.pin_manual})`,
          value: user.id,
        }));
    },
    [users, mappingContext.mappings, mappingRows]
  );

  const loadMachines = () => {
    makeRequest(setLoader, getAdmsMachinesReq, null, (data) => {
      const machineList = data.machines || [];
      setMachines(machineList);
      if (!selectedMachineId && machineList.length) {
        setSelectedMachineId(machineList[0].id);
      }
      if (selectedMachineId && !machineList.some((machine) => machine.id === selectedMachineId)) {
        setSelectedMachineId(machineList[0]?.id || null);
      }
    }, onError);
  };

  const getAttendancePayload = (machineId, range = attendanceRange) => ({
    machine_id: machineId,
    from_date: range?.[0] ? range[0].format("YYYY-MM-DD") : undefined,
    to_date: range?.[1] ? range[1].format("YYYY-MM-DD") : undefined,
  });

  const unwrapResponse = (response) => {
    if (response?.code === 200 || response?.success === true) {
      return response.data || {};
    }

    throw response;
  };

  const loadMachineDetails = async (machineId) => {
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setDetailLoader(true);

    try {
      const [usersRes, attendanceRes, commandsRes, requestsRes, mappingRes] = await Promise.all([
        getAdmsUsersReq(machineId),
        getAdmsAttendanceReq(getAttendancePayload(machineId)),
        getAdmsCommandsReq(machineId),
        getAdmsRequestsReq(machineId),
        getAdmsMappingContextReq(machineId),
      ]);

      if (detailRequestRef.current !== requestId) {
        return;
      }

      const usersData = unwrapResponse(usersRes);
      const attendanceData = unwrapResponse(attendanceRes);
      const commandsData = unwrapResponse(commandsRes);
      const requestsData = unwrapResponse(requestsRes);
      const mappingData = unwrapResponse(mappingRes);

      setUsers(usersData.users || []);
      setAttendanceLogs(attendanceData.attendance_logs || []);
      setCommands(commandsData.commands || []);
      setRequestLogs(requestsData.requests || []);
      setMappingContext({
        employees: mappingData.employees || [],
        mappings: mappingData.mappings || [],
      });
    } catch (err) {
      if (detailRequestRef.current === requestId) {
        onError(err?.data, err);
      }
    } finally {
      if (detailRequestRef.current === requestId) {
        setDetailLoader(false);
      }
    }
  };

  const loadAttendance = (machineId = selectedMachineId, range = attendanceRange, useDetailLoader = false) => {
    if (!machineId) {
      return;
    }

    makeRequest(useDetailLoader ? setDetailLoader : setLoader, getAdmsAttendanceReq, getAttendancePayload(machineId, range), (data) => {
      setAttendanceLogs(data.attendance_logs || []);
    }, onError);
  };

  const refreshSelectedMachine = () => {
    if (!selectedMachineId) {
      return;
    }

    loadMachineDetails(selectedMachineId);
  };

  const refreshMappingsOnly = () => {
    if (!selectedMachineId) {
      return;
    }

    makeRequest(setDetailLoader, getAdmsMappingContextReq, selectedMachineId, (data) => {
      setMappingContext({
        employees: data.employees || [],
        mappings: data.mappings || [],
      });
    }, onError);
  };

  const handleSyncUsers = () => {
    if (!selectedMachine) {
      return;
    }

    makeRequest(setActionLoader, syncAdmsUsersReq, { machine_id: selectedMachine.id, pin: "ALL" }, (data, res) => {
      message.success(res.message || "User sync queued successfully");
      setCommands(data.commands || []);
      refreshSelectedMachine();
    }, onError);
  };

  const handleFetchSingleUser = (user) => {
    makeRequest(setActionLoader, syncAdmsUsersReq, { machine_id: selectedMachine.id, pin: user.pin_manual }, (data, res) => {
      message.success(res.message || "User fetch queued successfully");
      setCommands(data.commands || []);
      refreshSelectedMachine();
    }, onError);
  };

  const handleSaveUser = (payload) => {
    makeRequest(setActionLoader, upsertAdmsUserReq, payload, (data, res) => {
      message.success(res.message || "User command queued successfully");
      setUserModalOpen(false);
      setEditingUser(null);
      setCommands(data.commands || []);
      refreshSelectedMachine();
    }, onError);
  };

  const handleDeleteUser = (user) => {
    makeRequest(setActionLoader, deleteAdmsUserReq, { machine_id: selectedMachine.id, pin: user.pin_manual }, (data, res) => {
      message.success(res.message || "User delete queued successfully");
      setCommands((currentCommands) => (data.command ? [data.command, ...currentCommands] : currentCommands));
      refreshSelectedMachine();
    }, onError);
  };

  const handleQueueCommand = (payload) => {
    makeRequest(setActionLoader, queueAdmsCommandReq, payload, (data, res) => {
      message.success(res.message || "Command queued successfully");
      setCommandModalOpen(false);
      setCommands((currentCommands) => (data.command ? [data.command, ...currentCommands] : currentCommands));
      refreshSelectedMachine();
    }, onError);
  };

  const handlePreviewEmployeeSync = (previewAll = false) => {
    if (!selectedMachine) {
      return;
    }

    makeRequest(setActionLoader, previewAdmsEmployeeSyncReq, {
      machine_id: selectedMachine.id,
      employee_ids: previewAll ? [] : selectedEmployeeIds,
    }, (data, res) => {
      setSyncPreview({
        rows: data.rows || [],
        summary: data.summary || null,
      });
      message.success(res.message || "Preview generated successfully");
    }, onError);
  };

  const handleSyncEmployees = () => {
    if (!selectedMachine) {
      return;
    }

    const rows = syncPreview.rows || [];
    if (!rows.length) {
      message.warning("Generate a preview before syncing employees.");
      return;
    }

    const syncableEmployeeIds = rows
      .filter((row) => row.status === "ready" || row.status === "map_existing_user" || row.status === "already_synced")
      .map((row) => row.employee_id);

    if (!syncableEmployeeIds.length) {
      message.warning("No preview rows are ready for sync.");
      return;
    }

    makeRequest(setActionLoader, syncAdmsEmployeesReq, {
      machine_id: selectedMachine.id,
      employee_ids: syncableEmployeeIds,
    }, (data, res) => {
      message.success(res.message || "Employees sync queued successfully");
      setCommands((currentCommands) => (data.commands || []).concat(currentCommands));
      refreshSelectedMachine();
    }, onError);
  };

  const updateMappingRow = (key, field, value) => {
    setMappingRows((currentRows) => currentRows.map((row) => (
      row.key === key ? { ...row, [field]: value } : row
    )));
  };

  const addMappingRow = () => {
    setMappingRows((currentRows) => [...currentRows, makeMappingRow()]);
  };

  const removeMappingRow = (key) => {
    setMappingRows((currentRows) => {
      const nextRows = currentRows.filter((row) => row.key !== key);
      return nextRows.length ? nextRows : [makeMappingRow()];
    });
  };

  const handleSaveMappings = () => {
    if (!selectedMachine) {
      return;
    }

    const mappings = mappingRows
      .filter((row) => row.employee_id && row.user_id)
      .map((row) => ({
        employee_id: row.employee_id,
        user_id: row.user_id,
      }));

    if (!mappings.length) {
      message.warning("Add at least one employee and machine user mapping row.");
      return;
    }

    makeRequest(setActionLoader, saveAdmsMappingsReq, {
      machine_id: selectedMachine.id,
      mappings,
    }, (data, res) => {
      message.success(res.message || "Mappings saved successfully");
      setMappingRows([makeMappingRow()]);
      refreshMappingsOnly();
      loadAttendance();
      setUsers((currentUsers) => currentUsers.map((user) => {
        const matched = (data.mappings || []).find((mapping) => mapping.user_id === user.id);
        return matched ? { ...user, machine_employee: matched } : user;
      }));
    }, onError);
  };

  const handleDeleteMapping = (mapping) => {
    makeRequest(setActionLoader, removeAdmsMappingReq, {
      machine_id: selectedMachine.id,
      employee_id: mapping.employee_id,
    }, (data, res) => {
      message.success(res.message || "Mapping removed successfully");
      refreshMappingsOnly();
      loadAttendance();
    }, onError);
  };

  const handleFilterAttendance = (range) => {
    setAttendanceRange(range || []);
    loadAttendance(selectedMachineId, range || [], true);
  };

  const handleFetchAttendance = () => {
    if (!selectedMachine) {
      return;
    }

    if (!attendanceRange?.[0] || !attendanceRange?.[1]) {
      message.warning("Select a from and to date before fetching attendance from the machine.");
      return;
    }

    makeRequest(setActionLoader, fetchAdmsAttendanceReq, {
      machine_id: selectedMachine.id,
      from_date: attendanceRange[0].format("YYYY-MM-DD"),
      to_date: attendanceRange[1].format("YYYY-MM-DD"),
    }, (data, res) => {
      message.success(res.message || "Attendance fetch command queued successfully");
      setCommands((currentCommands) => (data.command ? [data.command, ...currentCommands] : currentCommands));
      refreshSelectedMachine();
    }, onError);
  };

  const onError = (err, res) => {
    const errorMessage = err?.message || res?.response?.data?.message || res?.message || "Request failed";
    message.error(errorMessage);
  };

  const machineColumns = [
    {
      title: "Machine",
      key: "machine",
      render: (record) => (
        <div style={ { display: "flex", gap: 12, alignItems: "center" } }>
          <div style={ {
            width: 38,
            height: 38,
            borderRadius: 12,
            background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
            color: "#1d4ed8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          } }>
            <LaptopOutlined />
          </div>
          <div>
            <div style={ { fontWeight: 700, color: "#102a43" } }>{ record.name || record.code }</div>
            <div style={ { color: "#6b7c93", fontSize: 12 } }>{ record.serial_number || record.code }</div>
          </div>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: ["machine_type", "name"],
      render: (_, record) => record.machine_type?.name || "Zk Tech Adms",
    },
    {
      title: "Last Activity",
      dataIndex: "last_activity_at",
      render: (value) => (value ? convertTimeTotz(value).format("DD MMM YYYY hh:mm a") : "-"),
    },
    {
      title: "Status",
      key: "status",
      render: (record) => {
        const online = !!record.last_activity_at;
        return <Tag color={ online ? "green" : "red" }>{ online ? "Seen" : "Unknown" }</Tag>;
      },
    },
  ];

  const userColumns = [
    { title: "PIN", dataIndex: "pin_manual", key: "pin_manual" },
    {
      title: "Name",
      dataIndex: "user_name",
      key: "user_name",
      filters: userFilterOptions,
      filterSearch: true,
      onFilter: (value, record) => String(record.id) === String(value),
    },
    {
      title: "Mapped Employee",
      key: "mapped_employee",
      render: (record) => {
        const employeeName = record.machine_employee?.employee_name || record.employees?.[0]?.full_name || "-";
        const employeeCode = record.machine_employee?.employee_emp_code || record.machine_employee?.employee?.emp_code || record.employees?.[0]?.emp_code || "";

        return employeeCode && employeeName !== "-" ? `${employeeName} (${employeeCode})` : employeeName;
      },
      filters: mappedEmployeeFilterOptions,
      filterSearch: true,
      onFilter: (value, record) => String(record.machine_employee?.employee_id || record.employees?.[0]?.id || record.id) === String(value),
    },
    {
      title: "Privilege",
      dataIndex: "privilege",
      key: "privilege",
      render: (value) => privilegeLabelMap[value] || privilegeLabelMap[String(value)] || value || "-",
    },
    { title: "Card", dataIndex: "card", key: "card", render: (value) => value ?? "-" },
    // { title: "Group", dataIndex: "adms_acc_group", key: "adms_acc_group", render: (value) => value || "-" },
    {
      title: "Last Seen",
      dataIndex: "last_seen_at",
      key: "last_seen_at",
      render: (value) => (value ? convertTimeTotz(value).format("DD MMM YYYY hh:mm a") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space wrap>
          <Button size="small" onClick={ () => { setEditingUser(record); setUserModalOpen(true); } }>Edit</Button>
          <Button size="small" onClick={ () => handleFetchSingleUser(record) }>Fetch</Button>
          <Popconfirm title="Queue delete command for this user?" onConfirm={ () => handleDeleteUser(record) }>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const previewColumns = [
    { title: "Employee", dataIndex: "employee_name", key: "employee_name" },
    { title: "Employee Code", dataIndex: "emp_code", key: "emp_code" },
    { title: "Device PIN", dataIndex: "pin", key: "pin" },
    { title: "Machine User Name", dataIndex: "user_name", key: "user_name" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => <Tag color={ statusColors[value] || "default" }>{ value.replaceAll("_", " ") }</Tag>,
    },
    { title: "Message", dataIndex: "message", key: "message" },
  ];

  const mappingColumns = [
    {
      title: "Employee",
      key: "employee_name",
      render: (record) => record.employee_name || record.employee?.full_name || record.employee_id || "-",
      filters: mappingEmployeeFilterOptions,
      filterSearch: true,
      onFilter: (value, record) => String(record.employee_id) === String(value),
    },
    {
      title: "Machine User",
      dataIndex: "user_name_id",
      key: "user_name_id",
      filters: mappingUserFilterOptions,
      filterSearch: true,
      onFilter: (value, record) => String(record.user_id) === String(value),
    },
    { title: "Card", dataIndex: "card", key: "card", render: (value) => value || "-" },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Popconfirm title="Remove this employee mapping?" onConfirm={ () => handleDeleteMapping(record) }>
          <Button size="small" danger>Remove</Button>
        </Popconfirm>
      ),
    },
  ];

  const attendanceColumns = [
    { title: "Employee PIN", dataIndex: "user_id", key: "user_id" },
    { title: "Employee", key: "employee", render: (record) => record.employee?.full_name || "-" },
    {
      title: "Punch Time",
      dataIndex: "punch_time",
      key: "punch_time",
      render: (value) => (value ? convertTimeTotz(value).format("DD MMM YYYY hh:mm a") : "-"),
    },
    { title: "Punch Type", dataIndex: "punch_type", key: "punch_type" },
    { title: "Verify", dataIndex: "adms_verify_type", key: "adms_verify_type", render: (value) => value || "-" },
    { title: "Status Code", dataIndex: "adms_status_code", key: "adms_status_code", render: (value) => value || "-" },
  ];

  const commandColumns = [
    { title: "ID", dataIndex: "id", key: "id", width: 80 },
    { title: "Command", dataIndex: "command", key: "command" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => <Badge count={ value } style={ { backgroundColor: value === "completed" ? "#52c41a" : value === "sent" ? "#1677ff" : "#faad14" } } />,
    },
    {
      title: "Sent At",
      dataIndex: "sent_at",
      key: "sent_at",
      render: (value) => (value ? convertTimeTotz(value).format("DD MMM YYYY hh:mm a") : "-"),
    },
  ];

  const requestColumns = [
    { title: "Method", dataIndex: "method", key: "method", width: 100 },
    { title: "Path", dataIndex: "path", key: "path" },
    { title: "Serial", dataIndex: "serial_number", key: "serial_number" },
    {
      title: "Time",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (value ? convertTimeTotz(value).format("DD MMM YYYY hh:mm a") : "-"),
    },
  ];

  return (
    <>
      <AdmsUserModal
        open={ userModalOpen }
        loading={ actionLoader }
        machine={ selectedMachine }
        record={ editingUser }
        onCancel={ () => {
          setUserModalOpen(false);
          setEditingUser(null);
        } }
        onSubmit={ handleSaveUser }
      />

      <AdmsCommandModal
        open={ commandModalOpen }
        loading={ actionLoader }
        machine={ selectedMachine }
        onCancel={ () => setCommandModalOpen(false) }
        onSubmit={ handleQueueCommand }
      />

      <BodyComponent>
        <Row gutter={ [16, 16] }>
          <Col xs={ 24 } xl={ 7 }>
            <Card
              title={ <Space><LaptopOutlined style={ { color: "#1677ff" } } />ADMS Machines</Space> }
              extra={ <RefreshButton onClick={ loadMachines } /> }
              bodyStyle={ {
                padding: 0,
                height: viewportPanelHeight,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              } }
              style={ { ...softCardStyle, ...stickyPanelStyle } }
            >
              <Table
                rowKey="id"
                loading={ loader }
                columns={ machineColumns }
                dataSource={ machines }
                pagination={ false }
                size="small"
                scroll={ { y: "calc(100vh - 305px)" } }
                rowClassName={ (record) => record.id === selectedMachineId ? "ant-table-row-selected" : "" }
                onRow={ (record) => ({
                  onClick: () => setSelectedMachineId(record.id),
                  style: {
                    cursor: "pointer",
                    background: record.id === selectedMachineId ? "#edf5ff" : "transparent",
                  },
                }) }
              />
            </Card>
          </Col>

          <Col xs={ 24 } xl={ 17 }>
            <Card
              title={ selectedMachine ? `${selectedMachineLabel} ADMS Console` : "ADMS Console" }
              extra={
                <Space wrap>
                  <RefreshButton onClick={ refreshSelectedMachine } />
                  <Button type="primary" onClick={ handleSyncUsers } disabled={ !selectedMachine } loading={ actionLoader }>
                    Sync Users From Device
                  </Button>
                  <Button onClick={ () => { setEditingUser(null); setUserModalOpen(true); } } disabled={ !selectedMachine }>
                    Add User in Device
                  </Button>
                  <Button onClick={ () => setCommandModalOpen(true) } disabled={ !selectedMachine }>
                    Queue Command
                  </Button>
                </Space>
              }
              style={ { ...pageSurface, ...stickyPanelStyle } }
              headStyle={ {
                borderBottom: "1px solid #d9e4f5",
                background: "linear-gradient(90deg, #f8fbff 0%, #eef4ff 100%)",
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
              } }
              bodyStyle={ {
                height: viewportPanelHeight,
                overflowY: "auto",
                overflowX: "hidden",
              } }
            >
              { detailLoader ? (
                <div style={ {
                  minHeight: 320,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                } }>
                  <Spin size="large" tip="Loading ADMS console..." />
                </div>
              ) : !selectedMachine ? (
                <Empty description="No ADMS machine found. Create a machine with type Zk Tech Adms first." />
              ) : (
                <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
                  <Card
                    bordered={ false }
                    style={ {
                      borderRadius: 18,
                      background: "linear-gradient(135deg, #163d6b 0%, #0f62fe 60%, #4dabf7 100%)",
                      color: "#fff",
                      overflow: "hidden",
                    } }
                    bodyStyle={ { padding: 20 } }
                  >
                    <Row gutter={ [16, 16] } align="middle">
                      <Col xs={ 24 } lg={ 16 }>
                        <Space direction="vertical" size={ 6 }>
                          <Space wrap>
                            <Tag color="rgba(255,255,255,0.18)" style={ { borderRadius: 999, paddingInline: 10, color: "black", border: "1px solid rgba(255,255,255,0.2)" } }>
                              <SafetyCertificateOutlined /> ZK Tech ADMS
                            </Tag>
                            <Tag color="rgba(255,255,255,0.18)" style={ { borderRadius: 999, paddingInline: 10, color: "black", border: "1px solid rgba(255,255,255,0.2)" } }>
                              { selectedMachine.serial_number || selectedMachine.code }
                            </Tag>
                          </Space>
                          <Typography.Title level={ 3 } style={ { color: "#fff", margin: 0 } }>
                            { selectedMachine.name || selectedMachine.code }
                          </Typography.Title>
                          <Text style={ { color: "rgba(255,255,255,0.82)" } }>
                            Manage device users, attendance recovery, employee mapping, and command flow from one ADMS workspace.
                          </Text>
                        </Space>
                      </Col>
                      <Col xs={ 24 } lg={ 8 }>
                        <div style={ {
                          borderRadius: 16,
                          padding: 16,
                          background: "rgba(255,255,255,0.12)",
                          border: "1px solid rgba(255,255,255,0.18)",
                        } }>
                          <div style={ { display: "flex", justifyContent: "space-between", marginBottom: 8, color: "rgba(255,255,255,0.82)" } }>
                            <span>Connectivity</span>
                            <Tag color={ selectedMachine.last_activity_at ? "success" : "default" } style={ { marginInlineEnd: 0, borderRadius: 999 } }>
                              { selectedMachine.last_activity_at ? "Seen" : "Unknown" }
                            </Tag>
                          </div>
                          <div style={ { fontSize: 13, color: "#fff" } }>IP: { selectedMachine.ip || "-" }</div>
                          <div style={ { fontSize: 13, color: "rgba(255,255,255,0.82)", marginTop: 6 } }>
                            Last activity: { selectedMachine.last_activity_at ? convertTimeTotz(selectedMachine.last_activity_at).format("DD MMM YYYY hh:mm a") : "-" }
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card>

                  <Descriptions bordered size="small" column={ 2 }>
                    <Descriptions.Item label="Machine Code">{ selectedMachine.code || "-" }</Descriptions.Item>
                    <Descriptions.Item label="Serial Number">{ selectedMachine.serial_number || "-" }</Descriptions.Item>
                    <Descriptions.Item label="IP Address">{ selectedMachine.ip || "-" }</Descriptions.Item>
                    <Descriptions.Item label="Push Version">{ selectedMachine.adms_push_version || "-" }</Descriptions.Item>
                    {/* <Descriptions.Item label="Language">{ selectedMachine.adms_language || "-" }</Descriptions.Item> */ }
                    <Descriptions.Item label="Last Activity">
                      { selectedMachine.last_activity_at ? convertTimeTotz(selectedMachine.last_activity_at).format("DD MMM YYYY hh:mm a") : "-" }
                    </Descriptions.Item>
                  </Descriptions>

                  {/* <Row gutter={ 16 }>
                    <Col xs={ 24 } md={ 6 }>
                      <Card size="small" style={ { ...softCardStyle, background: statShells[0].tint, borderColor: statShells[0].border } }>
                        <Space direction="vertical" size={ 8 } style={ { width: "100%" } }>
                          { statShells[0].icon }
                          <Statistic title="Users" value={ users.length } />
                        </Space>
                      </Card>
                    </Col>
                    <Col xs={ 24 } md={ 6 }>
                      <Card size="small" style={ { ...softCardStyle, background: statShells[1].tint, borderColor: statShells[1].border } }>
                        <Space direction="vertical" size={ 8 } style={ { width: "100%" } }>
                          { statShells[1].icon }
                          <Statistic title="Mappings" value={ mappingContext.mappings.length } />
                        </Space>
                      </Card>
                    </Col>
                    <Col xs={ 24 } md={ 6 }>
                      <Card size="small" style={ { ...softCardStyle, background: statShells[2].tint, borderColor: statShells[2].border } }>
                        <Space direction="vertical" size={ 8 } style={ { width: "100%" } }>
                          { statShells[2].icon }
                          <Statistic title="Attendance Logs" value={ attendanceLogs.length } />
                        </Space>
                      </Card>
                    </Col>
                    <Col xs={ 24 } md={ 6 }>
                      <Card size="small" style={ { ...softCardStyle, background: statShells[3].tint, borderColor: statShells[3].border } }>
                        <Space direction="vertical" size={ 8 } style={ { width: "100%" } }>
                          { statShells[3].icon }
                          <Statistic title="Queued Commands" value={ commands.filter((item) => item.status !== "completed").length } />
                        </Space>
                      </Card>
                    </Col>
                  </Row> */}

                  <Tabs
                    type="card"
                    size="large"
                    tabBarGutter={ 8 }
                    tabBarStyle={ {
                      marginBottom: 16,
                      background: "linear-gradient(90deg, #edf4ff 0%, #f8fbff 100%)",
                      padding: 10,
                      borderRadius: 18,
                      border: "1px solid #d9e4f5",
                    } }
                    style={ {
                      background: "#fff",
                      borderRadius: 18,
                      padding: 10,
                      border: "1px solid #deebf7",
                    } }
                    items={ [
                      {
                        key: "users",
                        label: <span style={ { ...tabLabelStyle, background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", color: "#1d4ed8" } }><TeamOutlined />Users ({ users.length })</span>,
                        children: (
                          <Table
                            rowKey="id"
                            size="small"
                            columns={ userColumns }
                            dataSource={ users }
                            pagination={ { pageSize: 8 } }
                            scroll={ { x: 1000 } }
                          />
                        ),
                      },
                      {
                        key: "sync-mapping",
                        label: <span style={ { ...tabLabelStyle, background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", color: "#6d28d9" } }><UserSwitchOutlined />Mapping ({ mappingContext.mappings.length })</span>,
                        children: (
                          <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
                            <Card
                              size="small"
                              title={ <Space><ReloadOutlined style={ { color: "#1677ff" } } />Sync Existing Employees To Machine</Space> }
                              extra={
                                <Space wrap>
                                  <Button size="small" onClick={ () => handlePreviewEmployeeSync(false) } loading={ actionLoader }>
                                    Preview Selected
                                  </Button>
                                  <Button size="small" onClick={ () => handlePreviewEmployeeSync(true) } loading={ actionLoader }>
                                    Preview All
                                  </Button>
                                  <Button type="primary" size="small" onClick={ handleSyncEmployees } loading={ actionLoader }>
                                    Save & Sync Preview
                                  </Button>
                                </Space>
                              }
                              style={ softCardStyle }
                            >
                              <Space direction="vertical" style={ { width: "100%" } } size={ 12 }>
                                <Alert
                                  type="info"
                                  showIcon
                                  message="Preview first, then push only the valid employee records to the ADMS machine."
                                  style={ { borderRadius: 12 } }
                                />
                                <div>
                                  <Text strong>Select employees</Text>
                                  <Select
                                    mode="multiple"
                                    allowClear
                                    style={ { width: "100%", marginTop: 8 } }
                                    placeholder="Select employees to preview or leave blank and use Preview All"
                                    options={ employeeOptions }
                                    value={ selectedEmployeeIds }
                                    onChange={ setSelectedEmployeeIds }
                                    optionFilterProp="label"
                                  />
                                </div>

                                { syncPreview.summary ? (
                                  <Row gutter={ [8, 8] }>
                                    <Col xs={ 12 } md={ 6 }><Statistic title="Ready" value={ syncPreview.summary.ready || 0 } /></Col>
                                    <Col xs={ 12 } md={ 6 }><Statistic title="Map Existing" value={ syncPreview.summary.map_existing_user || 0 } /></Col>
                                    <Col xs={ 12 } md={ 6 }><Statistic title="Synced" value={ syncPreview.summary.already_synced || 0 } /></Col>
                                    <Col xs={ 12 } md={ 6 }><Statistic title="Conflicts" value={ syncPreview.summary.conflict || 0 } /></Col>
                                  </Row>
                                ) : null }

                                <Table
                                  rowKey="employee_id"
                                  size="small"
                                  columns={ previewColumns }
                                  dataSource={ syncPreview.rows }
                                  pagination={ { pageSize: 5 } }
                                  scroll={ { x: 900 } }
                                  locale={ { emptyText: "Generate a preview to review the employee-to-user sync before saving." } }
                                />
                              </Space>
                            </Card>

                            <Card
                              size="small"
                              title={ <Space><UserSwitchOutlined style={ { color: "#1677ff" } } />Manual Multi-User Mapping</Space> }
                              extra={
                                <Space wrap>
                                  <Button size="small" onClick={ addMappingRow }>Add Row</Button>
                                  <Button type="primary" size="small" onClick={ handleSaveMappings } loading={ actionLoader }>
                                    Save Mappings
                                  </Button>
                                </Space>
                              }
                              style={ softCardStyle }
                            >
                              <Space direction="vertical" style={ { width: "100%" } } size={ 12 }>
                                <Alert
                                  type="warning"
                                  showIcon
                                  icon={ <WarningOutlined /> }
                                  message="Only unmapped employees and unmapped machine users are shown here to avoid duplicate links."
                                  style={ { borderRadius: 12 } }
                                />
                                { mappingRows.map((row, index) => (
                                  <Row gutter={ [8, 8] } key={ row.key } align="middle">
                                    <Col xs={ 24 } lg={ 11 }>
                                      <Select
                                        allowClear
                                        showSearch
                                        placeholder={ `Employee ${index + 1}` }
                                        style={ { width: "100%" } }
                                        options={ employeeOptions }
                                        value={ row.employee_id }
                                        onChange={ (value) => updateMappingRow(row.key, "employee_id", value) }
                                        optionFilterProp="label"
                                      />
                                    </Col>
                                    <Col xs={ 24 } lg={ 11 }>
                                      <Select
                                        allowClear
                                        showSearch
                                        placeholder="Machine user"
                                        style={ { width: "100%" } }
                                        options={ machineUserOptions }
                                        value={ row.user_id }
                                        onChange={ (value) => updateMappingRow(row.key, "user_id", value) }
                                        optionFilterProp="label"
                                      />
                                    </Col>
                                    <Col xs={ 24 } lg={ 2 }>
                                      <Button danger block onClick={ () => removeMappingRow(row.key) }>
                                        Remove
                                      </Button>
                                    </Col>
                                  </Row>
                                )) }

                                <Table
                                  rowKey={ (record) => `${record.machine_id}-${record.employee_id}` }
                                  size="small"
                                  columns={ mappingColumns }
                                  dataSource={ mappingContext.mappings }
                                  pagination={ { pageSize: 5 } }
                                  scroll={ { x: 700 } }
                                  locale={ { emptyText: "No employee mappings yet for this ADMS machine." } }
                                />
                              </Space>
                            </Card>
                          </Space>
                        ),
                      },
                      {
                        key: "attendance",
                        label: <span style={ { ...tabLabelStyle, background: "linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)", color: "#0f766e" } }><DatabaseOutlined />Attendance Logs ({ attendanceLogs.length })</span>,
                        children: (
                          <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
                            <Card
                              size="small"
                              title={ <Space><DatabaseOutlined style={ { color: "#1677ff" } } />Attendance Recovery</Space> }
                              extra={
                                <Space wrap>
                                  <Button size="small" onClick={ () => handleFilterAttendance(attendanceRange) }>
                                    Filter Stored Logs
                                  </Button>
                                  <Button type="primary" size="small" onClick={ handleFetchAttendance } loading={ actionLoader }>
                                    Fetch From Machine
                                  </Button>
                                </Space>
                              }
                              style={ softCardStyle }
                            >
                              <Space direction="vertical" style={ { width: "100%" } } size={ 12 }>
                                <Text type="secondary">
                                  Use the date range to review saved logs or queue a device-side ATTLOG fetch when pushes are missing.
                                </Text>
                                <RangePicker
                                  style={ { width: "100%" } }
                                  value={ attendanceRange?.length ? attendanceRange : null }
                                  onChange={ (value) => setAttendanceRange(value || []) }
                                  allowClear
                                />
                              </Space>
                            </Card>

                            <Table
                              rowKey="id"
                              size="small"
                              columns={ attendanceColumns }
                              dataSource={ attendanceLogs }
                              pagination={ { pageSize: 8 } }
                              scroll={ { x: 900 } }
                            />
                          </Space>
                        ),
                      },
                      {
                        key: "commands",
                        label: <span style={ { ...tabLabelStyle, background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)", color: "#c2410c" } }><ApiOutlined />Commands ({ commands.length })</span>,
                        children: (
                          <Table
                            rowKey="id"
                            size="small"
                            columns={ commandColumns }
                            dataSource={ commands }
                            pagination={ { pageSize: 8 } }
                            scroll={ { x: 900 } }
                          />
                        ),
                      },
                      {
                        key: "requests",
                        label: <span style={ { ...tabLabelStyle, background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)", color: "#166534" } }><SafetyCertificateOutlined />Requests ({ requestLogs.length })</span>,
                        children: (
                          <Table
                            rowKey="id"
                            size="small"
                            columns={ requestColumns }
                            dataSource={ requestLogs }
                            pagination={ { pageSize: 8 } }
                            scroll={ { x: 900 } }
                          />
                        ),
                      },
                    ] }
                  />
                </Space>
              ) }
            </Card>
          </Col>
        </Row>
      </BodyComponent>
    </>
  );
};

export default IndexAdmsMachines;
